import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { DynamicRouteService, HeaderService, Q8Breakpoints } from 'core';
import { RECEIPTS_DESCRIBE } from '../constants';
import { toRouteSegments } from '../../utils/receipt.utils';
import {
  LoadingSpinner,
  SortColumn,
  TableColumnInfo,
  TableView,
  TypeOfColumn,
} from 'design-system';
import { finalize, map, Observable, startWith } from 'rxjs';
import {
  CustomerFilterOptions,
  CustomerLookupOption,
  CustomerZoneOption,
  receiptsFilterDialogComponent,
} from '../../components/dialog-components/receipts-filter-dialog-component/receipts-filter-dialog-component';
import { receiptsCustomerAlertCell } from '../../components/receipts-customer-alert-cell/receipts-customer-alert-cell';
import { receiptsCustomerPriceListCell } from '../../components/receipts-customer-price-list-cell/receipts-customer-price-list-cell';

@Component({
  selector: 'rev-receipts-new',
  imports: [
    AsyncPipe,
    LoadingSpinner,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    ReactiveFormsModule,
    TranslocoCurrencyPipe,
    receiptsCustomerAlertCell,
    receiptsCustomerPriceListCell,
    TableView,
  ],
  templateUrl: './receipts-new.html',
  styleUrl: './receipts-new.scss',
})
export class ReceiptsNew implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('loadMoreTrigger')
  private loadMoreTrigger!: ElementRef<HTMLElement>;

  customers: any[] = [];
  customersLoading = false;
  totalItems = 0;
  desktopCustomers: any[] = [];
  desktopCustomersLoading = false;
  desktopTotalItems = 0;
  desktopPageIndex = 0;
  readonly desktopPageSize = 10;
  currentSortColumns: SortColumn[] = [];
  isDesktop = false;
  filtersOpen = false;
  filtersForm!: FormGroup;
  filteredZona!: Observable<CustomerZoneOption[]>;
  filteredTipoZona!: Observable<string[]>;
  filteredAgente!: Observable<CustomerLookupOption[]>;
  filteredIspettore!: Observable<CustomerLookupOption[]>;
  readonly filterOptions: CustomerFilterOptions = {
    zona: [],
    tipoZona: [],
    agente: [],
    ispettore: [],
  };

  readonly columnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'ragioneSociale',
      label: 'Ragione sociale',
      type: TypeOfColumn.TEXT,
      labelOnOneLine: true,
      sortable: true,
    },
    {
      columnNameDef: 'localita',
      label: 'Località',
      type: TypeOfColumn.TEXT,
      sortable: true,
    },
    {
      columnNameDef: 'zonaAgente',
      label: 'Zona / Agente',
      type: TypeOfColumn.TEXT,
    },
    {
      columnNameDef: 'tipoListino',
      label: 'Tipo listino',
      type: TypeOfColumn.COMPONENT,
      component: receiptsCustomerPriceListCell,
    },
    {
      columnNameDef: 'esposizione',
      label: 'Esposizione',
      type: TypeOfColumn.CURRENCY,
      labelOnOneLine: true,
      cellClass: row => this.getCustomerStatusClass(row.esposizioneStato),
    },
    {
      columnNameDef: 'affidamento',
      label: 'Affidamento',
      type: TypeOfColumn.CURRENCY,
      labelOnOneLine: true,
      cellClass: row => this.getCustomerStatusClass(row.affidamentoStato),
    },
    {
      columnNameDef: 'fatturatoRolling12m',
      label: 'Fatturato rolling 12 mesi',
      type: TypeOfColumn.CURRENCY,
    },
    {
      columnNameDef: 'dataUltimaFattura',
      label: 'Data ultima fattura',
      type: TypeOfColumn.DATE,
    },
    {
      columnNameDef: 'alert',
      label: 'Alert',
      type: TypeOfColumn.COMPONENT,
      component: receiptsCustomerAlertCell,
    },
  ];

  private pageIndex = 0;
  private readonly pageSize = 20;
  private customersInitialized = false;
  private desktopCustomersInitialized = false;
  private customersLoadRequestId = 0;
  private desktopCustomersLoadRequestId = 0;
  private customersObserver?: IntersectionObserver;
  private filtersDialogRef?: MatDialogRef<receiptsFilterDialogComponent>;
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly dynamicRouteService = inject(DynamicRouteService);

  ngOnInit(): void {
    this.headerService.setTitle('Nuovo incasso');
    this.initializeFilters();
    this.loadFilterOptions();

    this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.tablet - 1}px)`])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matches }) => {
        if (matches) {
          this.headerService.setActions([{
            label: 'Mostra filtri',
            icon: 'fa-filter',
            action: () => this.openFiltersDialog(),
          }]);
          this.refreshCustomersObserver();
          return;
        }

        this.filtersDialogRef?.close();
        this.headerService.removeActions();
      });

    this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.desktop}px)`])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matches }) => {
        this.isDesktop = matches;

        if (matches) {
          if (!this.desktopCustomersInitialized) {
            this.loadDesktopCustomers();
          }
          return;
        }

        if (!this.customersInitialized) {
          this.loadCustomers(0, false);
        }
        this.refreshCustomersObserver();
      });
  }

  ngAfterViewInit(): void {
    this.customersObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadNextCustomersPage();
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0,
      },
    );

    this.customersObserver.observe(this.loadMoreTrigger.nativeElement);
  }

  ngOnDestroy(): void {
    this.customersObserver?.disconnect();
    this.customersObserver = undefined;
    this.filtersDialogRef?.close();
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  get isInitialLoading(): boolean {
    return this.customersLoading && !this.customersInitialized;
  }

  get isLoadingMore(): boolean {
    return this.customersLoading && this.customersInitialized;
  }

  get customersEmpty(): boolean {
    return this.customersInitialized &&
      !this.customersLoading &&
      this.customers.length === 0;
  }

  get desktopCustomersEmpty(): boolean {
    return this.desktopCustomersInitialized &&
      !this.desktopCustomersLoading &&
      this.desktopCustomers.length === 0;
  }

  getCustomerAlertIcon(alertStatus?: string | null): string {
    switch (alertStatus) {
      case 'Info':
        return 'fa-circle-info';
      case 'Warning':
      case 'warn':
        return 'fa-triangle-exclamation';
      case 'Danger':
      case 'err':
        return 'fa-circle-exclamation';
      case 'Success':
      case 'ok':
      default:
        return 'fa-circle-check';
    }
  }

  getCustomerAlertClass(alertStatus?: string | null): string {
    switch (alertStatus) {
      case 'Info':
        return 'customer-alert-info';
      case 'Warning':
      case 'warn':
        return 'customer-alert-warning';
      case 'Danger':
      case 'err':
        return 'customer-alert-danger';
      case 'Success':
      case 'ok':
      default:
        return 'customer-alert-success';
    }
  }

  getCustomerPriceListClass(priceList?: string | null): string {
    switch (priceList) {
      case 'Oil':
        return 'customer-price-list-oil';
      case 'NonOil':
        return 'customer-price-list-non-oil';
      case 'agenti':
        return 'customer-price-list-agents';
      default:
        return 'customer-price-list-free';
    }
  }

  getCustomerPriceListLabel(priceList?: string | null): string {
    switch (priceList) {
      case 'Oil':
        return 'Oil';
      case 'NonOil':
        return 'Non Oil';
      case 'agenti':
        return 'Agente';
      default:
        return 'Libero';
    }
  }

  getCustomerStatusClass(status?: string | null): string {
    switch (status) {
      case 'F':
        return 'currency-red';
      case 'o':
        return 'currency-orange';
      case 'y':
        return 'currency-yellow';
      case 'g':
        return 'currency-green';
      default:
        return '';
    }
  }

  onDesktopPageChange(event: PageEvent): void {
    this.desktopPageIndex = event.pageIndex;
    this.loadDesktopCustomers();
  }

  onDesktopSortChange(sortColumns: SortColumn[]): void {
    this.currentSortColumns = sortColumns;
    this.desktopPageIndex = 0;
    this.loadDesktopCustomers();
  }

  selectCustomer(customer: any): void {
    const idBusinessPartner = customer?.idBusinessPartner;

    if (idBusinessPartner === undefined || idBusinessPartner === null) {
      return;
    }

    const navigationArray = this.dynamicRouteService.getRoutePath(
      this.router,
      RECEIPTS_DESCRIBE,
      'new-customer-summary',
    );

    if (!navigationArray) {
      console.error('Rotta new-customer-summary non configurata');
      return;
    }

    void this.router.navigate(
      this.dynamicRouteService.replacePathSegments(
        toRouteSegments(navigationArray),
        [idBusinessPartner],
      ),
    );
  }

  toggleDesktopFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  applyFilters(): void {
    this.reloadCustomersWithFilters();
  }

  resetFilters(): void {
    this.filtersForm.patchValue({
      agente: '',
      agenteId: '',
      ispettore: '',
      ispettoreId: '',
      tipoZona: [],
      zona: '',
      zonaId: '',
      dataUltimaFattura: null,
      newCustomer: false,
    });
    this.reloadCustomersWithFilters();
  }

  clearFilterField(controlName: string): void {
    const control = this.filtersForm.get(controlName);
    if (!control) {
      return;
    }

    control.setValue(controlName === 'tipoZona' ? [] : '');
    if (controlName === 'zona' || controlName === 'agente' || controlName === 'ispettore') {
      this.filtersForm.get(`${controlName}Id`)?.setValue('');
    }
  }

  onZonaSelected(zone: CustomerZoneOption): void {
    this.filtersForm.patchValue({ zona: zone.nome, zonaId: zone.id });
  }

  onAgenteSelected(agente: CustomerLookupOption): void {
    this.filtersForm.patchValue({ agente: agente.label, agenteId: agente.value });
  }

  onIspettoreSelected(ispettore: CustomerLookupOption): void {
    this.filtersForm.patchValue({ ispettore: ispettore.label, ispettoreId: ispettore.value });
  }

  private openFiltersDialog(): void {
    if (this.filtersDialogRef) {
      return;
    }

    this.filtersDialogRef = this.dialog.open(receiptsFilterDialogComponent, {
      data: {
        value: this.filtersForm.getRawValue(),
        options: this.filterOptions,
      },
    });
    this.filtersDialogRef.afterClosed().subscribe(result => {
      this.filtersDialogRef = undefined;

      if (result?.action === 'reset') {
        this.resetFilters();
      } else if (result?.action === 'apply') {
        this.filtersForm.patchValue(result.value, { emitEvent: false });
        this.applyFilters();
      }
    });
  }

  private initializeFilters(): void {
    this.filtersForm = this.formBuilder.group({
      agente: [''],
      agenteId: [''],
      ispettore: [''],
      ispettoreId: [''],
      tipoZona: [{ value: [] as string[], disabled: true }],
      zona: [''],
      zonaId: [''],
      dataUltimaFattura: [null as Date | null],
      newCustomer: [false],
    });

    this.filteredTipoZona = this.filtersForm.get('tipoZona')!.valueChanges.pipe(
      startWith(this.filtersForm.get('tipoZona')!.value ?? []),
      map(value => this.filterStringOptions(this.filterOptions.tipoZona, value)),
    );
    this.filteredZona = this.filtersForm.get('zona')!.valueChanges.pipe(
      startWith(this.filtersForm.get('zona')!.value ?? ''),
      map(value => this.filterZoneOptions(value)),
    );
    this.filteredAgente = this.filtersForm.get('agente')!.valueChanges.pipe(
      startWith(this.filtersForm.get('agente')!.value ?? ''),
      map(value => this.filterLookupOptions(this.filterOptions.agente, value)),
    );
    this.filteredIspettore = this.filtersForm.get('ispettore')!.valueChanges.pipe(
      startWith(this.filtersForm.get('ispettore')!.value ?? ''),
      map(value => this.filterLookupOptions(this.filterOptions.ispettore, value)),
    );

    this.clearFilterIdWhenLabelChanges('zona', 'zonaId', this.filterOptions.zona, 'nome', 'id');
    this.clearFilterIdWhenLabelChanges('agente', 'agenteId', this.filterOptions.agente, 'label', 'value');
    this.clearFilterIdWhenLabelChanges('ispettore', 'ispettoreId', this.filterOptions.ispettore, 'label', 'value');
  }

  private loadFilterOptions(): void {
    this.http.get<{ items: CustomerZoneOption[] }>('api/lookup/zone').subscribe(response => {
      this.filterOptions.zona.splice(0, this.filterOptions.zona.length, ...(response.items ?? []));
      const zonaControl = this.filtersForm.get('zona')!;
      zonaControl.setValue(zonaControl.value);
    });
    this.http.get<{ agenti: CustomerLookupOption[] | null }>('api/lookup/agenti').subscribe(response => {
      this.filterOptions.agente.splice(0, this.filterOptions.agente.length, ...(response.agenti ?? []));
      const agenteControl = this.filtersForm.get('agente')!;
      agenteControl.setValue(agenteControl.value);
    });
    this.http.get<{ ispettori: CustomerLookupOption[] | null }>('api/lookup/ispettori').subscribe(response => {
      this.filterOptions.ispettore.splice(0, this.filterOptions.ispettore.length, ...(response.ispettori ?? []));
      const ispettoreControl = this.filtersForm.get('ispettore')!;
      ispettoreControl.setValue(ispettoreControl.value);
    });
  }

  private reloadCustomersWithFilters(): void {
    this.pageIndex = 0;
    this.desktopPageIndex = 0;

    if (this.isDesktop) {
      this.loadDesktopCustomers();
      return;
    }

    this.loadCustomers(0, false);
  }

  private filterZoneOptions(value: string | null): CustomerZoneOption[] {
    const search = (value ?? '').toLowerCase();
    return this.filterOptions.zona.filter(option => option.nome.toLowerCase().includes(search));
  }

  private filterStringOptions(options: string[], value: string | string[] | null): string[] {
    const search = Array.isArray(value) ? value.join(' ') : (value ?? '');
    return options.filter(option => option.toLowerCase().includes(search.toLowerCase()));
  }

  private filterLookupOptions(options: CustomerLookupOption[], value: string | null): CustomerLookupOption[] {
    const search = (value ?? '').toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(search));
  }

  private clearFilterIdWhenLabelChanges<T extends object>(
    labelControlName: string,
    idControlName: string,
    options: T[],
    labelProperty: keyof T,
    idProperty: keyof T,
  ): void {
    this.filtersForm.get(labelControlName)!.valueChanges.subscribe(value => {
      const selected = options.find(option => option[idProperty] === this.filtersForm.get(idControlName)!.value);
      if (selected?.[labelProperty] !== value) {
        this.filtersForm.get(idControlName)!.setValue('', { emitEvent: false });
      }
    });
  }

  private buildSearchPayload(orderBy: { PropertyName: string; OrderType: string }[]): object {
    const formValue = this.filtersForm.getRawValue();
    return {
      SearchText: { Text: '' },
      OrderBy: orderBy,
      searchObject: {
        ActiveCustomers: false,
        OrderInProgess: false,
        InvoicesExpiredLess30: false,
        InvoicesExpiredOver30: false,
        Agente: formValue.agenteId ? [formValue.agenteId] : [],
        Ispettore: formValue.ispettoreId ? [formValue.ispettoreId] : [],
        TipoZona: formValue.tipoZona ?? [],
        ZonaId: formValue.zonaId ? [formValue.zonaId] : [],
        DataUltimaFattura: formValue.dataUltimaFattura,
        NewCustomer: formValue.newCustomer ?? false,
      },
    };
  }

  private loadNextCustomersPage(): void {
    if (
      this.isDesktop ||
      !this.customersInitialized ||
      this.customersLoading ||
      this.customers.length >= this.totalItems
    ) {
      return;
    }

    this.loadCustomers(this.pageIndex + 1, true);
  }

  private loadCustomers(pageIndex: number, append: boolean): void {
    if (this.customersLoading) {
      return;
    }

    const endpoint = 'api/clienti/list';
    const requestId = ++this.customersLoadRequestId;
    this.customersLoading = true;

    const params = {
      'Pager.PageSize': this.pageSize,
      'Pager.Page': pageIndex + 1,
    };

    const payload = this.buildSearchPayload([]);

    this.http.post<any>(endpoint, payload, { params }).pipe(
      finalize(() => {
        if (requestId === this.customersLoadRequestId) {
          this.customersLoading = false;
          this.refreshCustomersObserver();
        }
      }),
    ).subscribe({
      next: data => {
        if (requestId !== this.customersLoadRequestId) {
          return;
        }

        const loadedCustomers = data.items.map((item: any) => ({
          ...item,
          zonaAgente: item.nomeAgente
            ? `${item.nomeZona}/${item.nomeAgente}`
            : `${item.nomeZona}`,
          esposizione: item.esposizione.valore,
          esposizioneStato: item.esposizione.stato,
          affidamento: item.affidamento.valore,
          affidamentoStato: item.affidamento.stato,
        }));

        this.customers = append
          ? this.appendUniqueCustomers(loadedCustomers)
          : loadedCustomers;
        this.pageIndex = pageIndex;
        this.totalItems = data.totalItems;
        this.customersInitialized = true;
      },
      error: () => {
        if (requestId !== this.customersLoadRequestId) {
          return;
        }

        if (!append) {
          this.customers = [];
          this.totalItems = 0;
        }

        this.customersInitialized = true;
      },
    });
  }

  loadDesktopCustomers(): void {
    if (this.desktopCustomersLoading) {
      return;
    }

    const endpoint = 'api/clienti/list';
    const requestId = ++this.desktopCustomersLoadRequestId;
    this.desktopCustomersLoading = true;

    const params = {
      'Pager.PageSize': this.desktopPageSize,
      'Pager.Page': this.desktopPageIndex + 1,
    };

    const payload = this.buildSearchPayload(
      this.currentSortColumns.map(sort => ({
        PropertyName: sort.column,
        OrderType: sort.direction === 'desc' ? 'Desc' : 'Asc',
      })),
    );

    this.http.post<any>(endpoint, payload, { params }).pipe(
      finalize(() => {
        if (requestId === this.desktopCustomersLoadRequestId) {
          this.desktopCustomersLoading = false;
        }
      }),
    ).subscribe({
      next: data => {
        if (requestId !== this.desktopCustomersLoadRequestId) {
          return;
        }

        this.desktopCustomers = this.mapCustomerItems(data.items);
        this.desktopTotalItems = data.totalItems;
        this.desktopCustomersInitialized = true;
      },
      error: () => {
        if (requestId !== this.desktopCustomersLoadRequestId) {
          return;
        }

        this.desktopCustomers = [];
        this.desktopTotalItems = 0;
        this.desktopCustomersInitialized = true;
      },
    });
  }

  private appendUniqueCustomers(loadedCustomers: any[]): any[] {
    const customerIds = new Set(
      this.customers.map(customer => customer.idPrimoCliente),
    );
    const uniqueCustomers = [...this.customers];

    for (const customer of loadedCustomers) {
      if (customerIds.has(customer.idPrimoCliente)) {
        continue;
      }

      customerIds.add(customer.idPrimoCliente);
      uniqueCustomers.push(customer);
    }

    return uniqueCustomers;
  }

  private mapCustomerItems(items: any[]): any[] {
    return items.map((item: any) => ({
      ...item,
      zonaAgente: item.nomeAgente
        ? `${item.nomeZona}/${item.nomeAgente}`
        : `${item.nomeZona}`,
      esposizione: item.esposizione.valore,
      esposizioneStato: item.esposizione.stato,
      affidamento: item.affidamento.valore,
      affidamentoStato: item.affidamento.stato,
    }));
  }

  private refreshCustomersObserver(): void {
    if (!this.customersObserver || !this.loadMoreTrigger) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.customersObserver || !this.loadMoreTrigger) {
        return;
      }

      this.customersObserver.unobserve(this.loadMoreTrigger.nativeElement);
      this.customersObserver.observe(this.loadMoreTrigger.nativeElement);
    });
  }
}
